import { useTranslation } from 'react-i18next';

import ScreenContainer from 'components/containers/ScreenContainer';
import CreateIcon from 'components/icons/CreateIcon';
import { useCallback, useEffect, useState } from 'react';
import ModalForm from 'components/ModalForm';
import { askUpdateCycle, createCycle, deleteCycle, fetchCycles, updateCycle } from 'lib/models/cycles';
import Alert, { AlertsTypes } from 'components/Alert';
import ConfirmationDialog from 'components/ConfirmationDialog';
import CyclesList from 'components/CyclesList';
import { useHistory } from 'react-router-dom';
import { Box, DialogContentText } from '@material-ui/core';

function Cycles() {
	const history = useHistory();
	const { t } = useTranslation();

	const [cycles, setCycles] = useState<CycleType[]>([]);

	const [cycle, setCycle] = useState<CycleType>({ name: '', id: '' });
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
	const [modifyQuantity, setModifyQuantity] = useState(0);
	const [deleted, setDeleted] = useState(false);

	const [error, setError] = useState('');

	const refetch = useCallback(async () => {
		setLoading(true);
		const response = await fetchCycles();
		if (response) {
			setCycles(response);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		refetch();
	}, [refetch]);

	const edit = async (cycle: CycleType) => {
		const { success, items } = await askUpdateCycle(cycle);
		if (success) {
			setModifyQuantity(items);
			setCycle(cycle);
			setOpen(true);
		}
	};

	const onDelete = async (cycle: CycleType) => {
		const { success, items } = await askUpdateCycle(cycle);
		if (success) {
			setModifyQuantity(items);
			setConfirmDeleteModal(true);
			setCycle(cycle);
		}
	};

	const save = async () => {
		setLoading(true);
		try {
			cycle._id ? await updateCycle(cycle) : await createCycle(cycle);
			setSuccess(true);
			refetch();
			closeModal();
		} catch (error) {
			console.error(error);
			setError(error && (error as any).message ? (error as any).message : error);
		}
		setLoading(false);
		resetAlert();
	};

	const onUpdate = async (value: string) => {
		setCycle((prev) => {
			return { ...prev, name: value };
		});
	};

	const confirmDelete = async (confirm: boolean) => {
		if (confirm) {
			try {
				await deleteCycle(cycle);
				setDeleted(true);
				setConfirmDeleteModal(false);
				refetch();
			} catch (error: any) {
				console.error(error);
				setError(error.message || error);
			}
		} else {
			setCycle({ name: '', id: '' });
			setConfirmDeleteModal(false);
		}
		resetAlert();
	};

	const closeModal = () => {
		setOpen(false);
		setCycle({ name: '', id: '' });
		setModifyQuantity(0);
	};

	const resetAlert = () => {
		setTimeout(() => {
			setSuccess(false);
			setError('');
			setDeleted(false);
		}, 3000);
	};

	const dialogDescription =
		modifyQuantity > 0
			? t('cycles:existent_items_on_delete_description')
			: t('cycles:delete_component_description', { quantity: modifyQuantity });
	return (
		<ScreenContainer title={t('cycles:cycle_plural')}>
			<CreateIcon
				onCreate={() => {
					setOpen(true);
				}}
			/>

			<CyclesList cycles={cycles} edit={edit} onDelete={onDelete} />

			{confirmDeleteModal && modifyQuantity === 0 && (
				<ConfirmationDialog onClose={confirmDelete} title={t('cycles:delete_cycle')} description={dialogDescription} />
			)}

			{confirmDeleteModal && modifyQuantity > 0 && (
				<ConfirmationDialog
					confirmButton={t('common:cancel')}
					onClose={() => {
						history.push(`/items?cycle=${cycle.name}`);
					}}
					hideCancel={true}
					title={t('system:errorTryingDelete')}
				>
					<Box>
						<DialogContentText>{t('cycles:existent_items_on_delete_title')}.</DialogContentText>
						<DialogContentText>{dialogDescription}</DialogContentText>
					</Box>
				</ConfirmationDialog>
			)}

			{open && (
				<ModalForm
					title={cycle.id ? t('cycles:edit_cycle') : t('cycles:add_cycle')}
					item={cycle}
					onUpdate={onUpdate}
					onSubmit={save}
					onCancel={closeModal}
					description={
						modifyQuantity > 0 ? t('cycles:edit_cycle_description', { quantity: modifyQuantity }) : undefined
					}
				/>
			)}
			{!loading && success && (
				<Alert
					message={t('common:success')}
					show={success}
					type={AlertsTypes.success}
					onClose={() => setSuccess(false)}
				/>
			)}

			{!loading && deleted && (
				<Alert
					message={t('common:deleted')}
					show={deleted}
					type={AlertsTypes.success}
					onClose={() => setDeleted(false)}
				/>
			)}

			{!loading && error && (
				<Alert message={error} show={!!error} type={AlertsTypes.error} onClose={() => setError('')} />
			)}
		</ScreenContainer>
	);
}

export default Cycles;
